import {
  NOTIFICATION_INBOX_MAX_ITEMS,
  NOTIFICATION_INBOX_SCHEMA_VERSION,
  NOTIFICATION_INBOX_SCHEMA_VERSION_PREVIOUS,
} from "./notificationConstants";

/**
 * @typedef {Object} InboxItem
 * @property {string} id
 * @property {number} createdAt
 * @property {'success'|'error'|'warning'|'info'} severity
 * @property {string} message
 * @property {boolean} read
 * @property {string[]|undefined} [actionIds]
 * @property {string|undefined} [actionRoute] - in-app path for CTA (allowlisted at click time)
 * @property {string|undefined} [actionLabel] - button label for actionRoute
 */

/**
 * @typedef {Object} InboxPersistedEnvelope
 * @property {number} schemaVersion
 * @property {InboxItem[]} items
 */

/**
 * Create a new inbox row (plain data only — no functions; safe for JSON).
 *
 * @param {Object} params
 * @param {string} params.id
 * @param {number} params.createdAt
 * @param {'success'|'error'|'warning'|'info'} params.severity
 * @param {string} params.message
 * @param {boolean} params.read
 * @param {string[]|undefined} [params.actionIds]
 * @param {string|undefined} [params.actionRoute]
 * @param {string|undefined} [params.actionLabel]
 * @returns {InboxItem}
 */
export function createInboxItem({ id, createdAt, severity, message, read, actionIds, actionRoute, actionLabel }) {
  const route = typeof actionRoute === "string" && actionRoute.trim().length > 0 ? actionRoute.trim() : undefined;
  const label = typeof actionLabel === "string" && actionLabel.trim().length > 0 ? actionLabel.trim() : undefined;
  return {
    id,
    createdAt,
    severity,
    message,
    read,
    ...(Array.isArray(actionIds) && actionIds.length > 0 ? { actionIds } : {}),
    ...(route ? { actionRoute: route } : {}),
    ...(label ? { actionLabel: label } : {}),
  };
}

/**
 * Enforce max length: drop oldest read first, then oldest unread (spec §6).
 *
 * @param {InboxItem[]} items
 * @param {number} [max]
 * @returns {InboxItem[]}
 */
export function capInboxItems(items, max = NOTIFICATION_INBOX_MAX_ITEMS) {
  if (items.length <= max) return items;
  const toRemove = items.length - max;
  const oldestFirst = [...items].sort((a, b) => a.createdAt - b.createdAt);
  const readOldest = oldestFirst.filter((i) => i.read);
  const unreadOldest = oldestFirst.filter((i) => !i.read);
  const removeIds = new Set();
  let n = 0;
  for (const row of readOldest) {
    if (n >= toRemove) break;
    removeIds.add(row.id);
    n++;
  }
  for (const row of unreadOldest) {
    if (n >= toRemove) break;
    removeIds.add(row.id);
    n++;
  }
  return items.filter((i) => !removeIds.has(i.id));
}

/**
 * Insert newest-first list semantics: prepend then cap.
 *
 * @param {InboxItem[]} items
 * @param {InboxItem} item
 * @returns {InboxItem[]}
 */
export function addInboxItem(items, item) {
  return capInboxItems([item, ...items]);
}

/**
 * @param {InboxItem[]} items
 * @param {string} id
 * @returns {InboxItem[]}
 */
export function dismissInboxItem(items, id) {
  return items.filter((i) => i.id !== id);
}

/**
 * @param {InboxItem[]} items
 * @param {string} id
 * @returns {InboxItem[]}
 */
export function markInboxItemRead(items, id) {
  return items.map((i) => (i.id === id && !i.read ? { ...i, read: true } : i));
}

/**
 * @param {InboxItem[]} items
 * @returns {InboxItem[]}
 */
export function markAllInboxRead(items) {
  return items.map((i) => (i.read ? i : { ...i, read: true }));
}

/**
 * @returns {InboxItem[]}
 */
export function clearAllInboxItems() {
  return [];
}

/**
 * @param {InboxItem[]} items
 * @returns {number}
 */
export function countUnreadInbox(items) {
  return items.filter((i) => !i.read).length;
}

/**
 * Sort for display: newest first.
 *
 * @param {InboxItem[]} items
 * @returns {InboxItem[]}
 */
export function sortInboxNewestFirst(items) {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Build persisted envelope for localStorage.
 *
 * @param {InboxItem[]} items
 * @returns {InboxPersistedEnvelope}
 */
export function serializeInboxEnvelope(items) {
  return {
    schemaVersion: NOTIFICATION_INBOX_SCHEMA_VERSION,
    items: capInboxItems(items),
  };
}

/**
 * Parse and validate loaded JSON. Returns items or null if unusable.
 *
 * @param {unknown} raw
 * @returns {InboxItem[]|null}
 */
export function parseInboxEnvelope(raw) {
  if (raw == null || typeof raw !== "object") return null;
  const schemaVersion = /** @type {{ schemaVersion?: unknown }} */ (raw).schemaVersion;
  const versionOk =
    schemaVersion === NOTIFICATION_INBOX_SCHEMA_VERSION || schemaVersion === NOTIFICATION_INBOX_SCHEMA_VERSION_PREVIOUS;
  if (!versionOk) return null;
  const items = /** @type {{ items?: unknown }} */ (raw).items;
  if (!Array.isArray(items)) return null;
  /** @type {InboxItem[]} */
  const out = [];
  for (const row of items) {
    if (row == null || typeof row !== "object") continue;
    const r = /** @type {Record<string, unknown>} */ (row);
    const id = typeof r.id === "string" ? r.id : "";
    const createdAt = typeof r.createdAt === "number" && Number.isFinite(r.createdAt) ? r.createdAt : 0;
    const sev = r.severity;
    const severity = sev === "success" || sev === "error" || sev === "warning" || sev === "info" ? sev : "info";
    const message = typeof r.message === "string" ? r.message : "";
    const read = Boolean(r.read);
    if (!id || !message) continue;
    const actionIds = Array.isArray(r.actionIds) ? r.actionIds.filter((x) => typeof x === "string") : undefined;
    const actionRoute = typeof r.actionRoute === "string" ? r.actionRoute : undefined;
    const actionLabel = typeof r.actionLabel === "string" ? r.actionLabel : undefined;
    out.push(createInboxItem({ id, createdAt, severity, message, read, actionIds, actionRoute, actionLabel }));
  }
  return capInboxItems(out);
}
